import { ErrorHandler, Injectable, inject } from '@angular/core';
import type { SupabaseClient } from '@supabase/supabase-js';
import { AuthStore } from '../../auth.store';
import { BrandSlug } from '../shared/brand-slug';
import { type EAN, tryEAN } from '../shared/ean';
import { OrgId, UserId } from '../shared/ids';
import { fromMasked, type MaskedEan, toMasked } from '../shared/masked-ean';
import { Segment } from './segment';
import { SegmentId } from './segment-id';
import {
  type SegmentCreateInput,
  type SegmentEvent,
  SegmentRepository,
  type Unsubscribe,
} from './segment-repository';
import { parseSpec, SkuPredicate } from './sku-predicate';

const TABLE = 'portal_comparison';
const SELECT_COLS =
  'id, brandalias, items, user_id, org_id, shared_with_org, spec, include, exclude';

type SegmentRow = {
  id: string;
  deleted_at?: string | null;
  brandalias: string | null;
  items: { products?: unknown; brands?: unknown; added_by?: unknown } | null;
  user_id: string | null;
  org_id: string | null;
  shared_with_org: boolean | null;
  spec: unknown;
  include: unknown;
  exclude: unknown;
};

@Injectable()
export class SupabaseSegmentRepository extends SegmentRepository {
  private readonly authStore = inject(AuthStore);
  private readonly errorHandler = inject(ErrorHandler);
  private nextGenId = 0;
  // Each member org's category_pseudobrands; a row is curated only when its own org lists it.
  private curatedByOrg: ReadonlyMap<OrgId, ReadonlySet<SegmentId>> = new Map();

  /**
   * Decodes the rows of one read under a single violation tally, so a stored pin that is
   * not a masked EAN is reported once with a count and never once per row.
   */
  private decodeRows<T>(decode: (skipped: SkippedPins) => T): T {
    const skipped: SkippedPins = { count: 0 };
    const decoded = decode(skipped);
    if (skipped.count > 0) {
      this.errorHandler.handleError(
        new Error(
          `[SegmentRepository] ${skipped.count} stored pin(s) in ${TABLE} are not masked EANs; skipped`
        )
      );
    }
    return decoded;
  }

  private client(): Promise<SupabaseClient> {
    return this.authStore.getSupabaseClient();
  }

  async getById(id: SegmentId): Promise<Segment | null> {
    const row = await this.getRowById(id);
    return row ? this.decodeRows((skipped) => rowToSegment(row, skipped, this.curatedByOrg)) : null;
  }

  // Raw row, because rowToSegment synthesizes a spec for legacy rows — only the
  // row itself still shows whether the migration to spec-bearing has happened.
  private async getRowById(id: SegmentId): Promise<SegmentRow | null> {
    const supabase = await this.client();
    const { data } = await supabase
      .from(TABLE)
      .select(SELECT_COLS)
      .eq('id', id)
      .is('deleted_at', null)
      .maybeSingle();
    return data ? (data as SegmentRow) : null;
  }

  private async requireRowById(id: SegmentId): Promise<SegmentRow> {
    const row = await this.getRowById(id);
    if (!row) throw new Error(`Segment "${id}" not found`);
    return row;
  }

  /**
   * The client, once its session is `userId`'s. Row visibility and ownership
   * are decided server-side from the session, so a read or write on behalf of
   * a user is only meaningful under that user's session.
   */
  private async clientAs(userId: UserId): Promise<SupabaseClient> {
    const supabase = await this.client();
    const {
      data: { session },
    } = await supabase.auth.getSession();
    if (session?.user.id !== userId) {
      throw new Error(`SegmentRepository: client is not signed in as ${userId}`);
    }
    return supabase;
  }

  async listForUser(userId: UserId): Promise<readonly Segment[]> {
    const supabase = await this.clientAs(userId);
    // Loaded before the rows, so a realtime event arriving during the row read classifies against it.
    this.curatedByOrg = await this.loadCuratedByOrg(supabase, userId);
    const { data, error } = await supabase
      .from(TABLE)
      .select(SELECT_COLS)
      .is('deleted_at', null)
      .order('created_at', { ascending: false });
    if (error) throw error;
    return this.decodeRows((skipped) =>
      ((data ?? []) as SegmentRow[]).map((row) => rowToSegment(row, skipped, this.curatedByOrg))
    );
  }

  // One PostgREST read embedding portal_org over the portal_org_member.org_id foreign key.
  private async loadCuratedByOrg(
    supabase: SupabaseClient,
    userId: UserId
  ): Promise<ReadonlyMap<OrgId, ReadonlySet<SegmentId>>> {
    const { data, error } = await supabase
      .from('portal_org_member')
      .select('org_id, portal_org(category_pseudobrands)')
      .eq('user_id', userId);
    if (error) {
      this.errorHandler.handleError(error);
      return new Map();
    }
    const byOrg = new Map<OrgId, ReadonlySet<SegmentId>>();
    for (const member of (data ?? []) as { org_id: unknown; portal_org: unknown }[]) {
      if (typeof member.org_id !== 'string' || member.org_id.trim() === '') continue;
      byOrg.set(OrgId(member.org_id), new Set(curatedIdsOf(member.portal_org)));
    }
    return byOrg;
  }

  async create(userId: UserId, input: SegmentCreateInput): Promise<Segment> {
    const supabase = await this.clientAs(userId);
    const slug = slugify(input.alias);
    const seedSkus = input.items?.skus ?? [];
    // D-07 exact projection: spec=false → items.brands=[], items.products=include.
    const items = buildItemsJson({
      skus: seedSkus,
      brands: [],
      addedBy: seedSkus.length ? new Map(seedSkus.map((e) => [e, userId] as const)) : null,
    });
    const { data, error } = await supabase
      .from(TABLE)
      .insert({
        user_id: userId,
        org_id: input.orgId,
        brandname: slug,
        brandalias: input.alias.trim(),
        shared_with_org: false,
        spec: false,
        include: storedPins(seedSkus),
        exclude: [],
        items,
      })
      .select(SELECT_COLS)
      .single();
    if (error || !data) throw error ?? new Error('SegmentRepository.create returned no row');
    return this.decodeRows((skipped) =>
      rowToSegment(data as SegmentRow, skipped, this.curatedByOrg)
    );
  }

  async rename(id: SegmentId, alias: string): Promise<Segment> {
    return this.updateAndReload(id, { brandalias: alias.trim() });
  }

  share(id: SegmentId): Promise<Segment> {
    return this.updateAndReload(id, { shared_with_org: true });
  }

  unshare(id: SegmentId): Promise<Segment> {
    return this.updateAndReload(id, { shared_with_org: false });
  }

  async addSkus(id: SegmentId, eans: readonly EAN[]): Promise<Segment> {
    if (eans.length === 0) return this.requireById(id);
    return this.modifyPins(id, 'add', eans);
  }

  async removeSkus(id: SegmentId, eans: readonly EAN[]): Promise<Segment> {
    if (eans.length === 0) return this.requireById(id);
    return this.modifyPins(id, 'remove', eans);
  }

  private async modifyPins(
    id: SegmentId,
    action: 'add' | 'remove',
    eans: readonly EAN[]
  ): Promise<Segment> {
    const row = await this.requireRowById(id);
    if (row.spec === null || row.spec === undefined) {
      await this.upgradeIfLegacy(
        id,
        this.decodeRows((skipped) => rowToSegment(row, skipped, this.curatedByOrg))
      );
    }
    const supabase = await this.client();
    const { error } = await supabase.rpc('segment_modify_pins', {
      p_segment_id: id,
      p_action: action,
      p_eans: storedPins(eans),
    });
    if (error) throw error;
    return this.requireById(id);
  }

  async attributionFor(id: SegmentId): Promise<ReadonlyMap<EAN, UserId>> {
    const supabase = await this.client();
    const { data } = await supabase
      .from(TABLE)
      .select('items')
      .eq('id', id)
      .is('deleted_at', null)
      .maybeSingle();
    return data ? rowToAttribution(data as { items: SegmentRow['items'] }) : new Map();
  }

  async replaceItems(
    id: SegmentId,
    params: { alias?: string; skus: readonly EAN[]; brands?: readonly BrandSlug[] }
  ): Promise<Segment> {
    // AI-populate: wholesale reset — spec false, include = kept EANs, exclude [].
    // D-07 exact projection: items.brands = [] (spec=false), items.products = include.
    const items = buildItemsJson({
      skus: params.skus,
      brands: [],
      addedBy: null,
    });
    const patch: Record<string, unknown> = {
      spec: false,
      include: storedPins(params.skus),
      exclude: [],
      items,
      ...(params.alias !== undefined ? { brandalias: params.alias.trim() } : {}),
    };
    return this.updateAndReload(id, patch);
  }

  // Soft delete: the table carries deleted_at and every read path filters on it
  // (same convention as portal_alert / portal_subscription). A hard DELETE has no
  // RLS policy behind it, so it silently matches zero rows and the row survives.
  async delete(id: SegmentId): Promise<void> {
    const supabase = await this.client();
    const { error } = await supabase
      .from(TABLE)
      .update({ deleted_at: new Date().toISOString() })
      .eq('id', id);
    if (error) throw error;
  }

  subscribeOwnership(userId: UserId, callback: (event: SegmentEvent) => void): Unsubscribe {
    const genId = this.nextGenId++;
    let handle: unknown = null;
    let alive = true;

    // genId in the channel name makes each subscription generation unique, preventing
    // the Supabase channel cache from returning an already-subscribed channel object
    // on re-subscribe (which would throw "cannot add postgres_changes callbacks after
    // subscribe()"). The .on() call happens before .subscribe() in the chain below.
    void (async () => {
      const supabase = await this.client();
      if (!alive) return;
      handle = supabase
        .channel(`segment-list-${userId}-${genId}`)
        .on(
          'postgres_changes' as never,
          { event: '*', schema: 'public', table: TABLE },
          (payload) => {
            if (!alive) return;
            const event = this.decodeRows((skipped) =>
              payloadToEvent(payload as unknown as RealtimePayload, skipped, this.curatedByOrg)
            );
            if (event) callback(event);
          }
        )
        .subscribe();
    })();

    return () => {
      alive = false;
      void (async () => {
        const supabase = await this.client();
        if (handle) await supabase.removeChannel(handle as never);
      })();
    };
  }

  private async requireById(id: SegmentId): Promise<Segment> {
    const segment = await this.getById(id);
    if (!segment) throw new Error(`Segment "${id}" not found`);
    return segment;
  }

  private async updateAndReload(id: SegmentId, patch: Record<string, unknown>): Promise<Segment> {
    const supabase = await this.client();
    const { error } = await supabase
      .from(TABLE)
      .update({ ...patch, updated_at: new Date().toISOString() })
      .eq('id', id);
    if (error) throw error;
    return this.requireById(id);
  }

  /**
   * Conditionally upgrades a legacy row (spec IS NULL) to spec-bearing before a
   * segment_modify_pins RPC call. The RPC initialises include from COALESCE(include, '[]'),
   * so a NULL include would desync include from items.products. The WHERE clause makes
   * this a no-op for rows that are already spec-bearing.
   */
  private async upgradeIfLegacy(id: SegmentId, current: Segment): Promise<void> {
    const supabase = await this.client();
    const { error } = await supabase
      .from(TABLE)
      .update({
        spec: current.definition.spec,
        include: storedPins(current.definition.include),
        exclude: [],
        updated_at: new Date().toISOString(),
      })
      .eq('id', id)
      .is('spec', null);
    if (error) throw error;
  }
}

type RealtimePayload = {
  eventType: 'INSERT' | 'UPDATE' | 'DELETE';
  new: SegmentRow | null;
  old: { id?: string } | null;
};

function payloadToEvent(
  payload: RealtimePayload,
  skipped: SkippedPins,
  curatedByOrg: ReadonlyMap<OrgId, ReadonlySet<SegmentId>>
): SegmentEvent | null {
  switch (payload.eventType) {
    case 'INSERT':
      return payload.new
        ? { kind: 'INSERT', segment: rowToSegment(payload.new, skipped, curatedByOrg) }
        : null;
    case 'UPDATE':
      if (!payload.new) return null;
      // A soft delete arrives as an UPDATE; report it as a removal so the row
      // leaves the list instead of being re-added by the UPDATE branch.
      if (payload.new.deleted_at) return { kind: 'DELETE', id: SegmentId(payload.new.id) };
      return { kind: 'UPDATE', segment: rowToSegment(payload.new, skipped, curatedByOrg) };
    case 'DELETE':
      return payload.old?.id ? { kind: 'DELETE', id: SegmentId(payload.old.id) } : null;
  }
}

/** How many stored pins one read had to skip. Mutated across the rows of that read only. */
type SkippedPins = { count: number };

// PostgREST embeds a many-to-one target as an object; the untyped client declares it an array.
function curatedIdsOf(embed: unknown): readonly SegmentId[] {
  const orgs = Array.isArray(embed) ? embed : [embed];
  return orgs.flatMap((org) =>
    org && typeof org === 'object'
      ? toSegmentIds((org as { category_pseudobrands?: unknown }).category_pseudobrands)
      : []
  );
}

function toSegmentIds(raw: unknown): readonly SegmentId[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((id): id is string => typeof id === 'string' && id.trim() !== '')
    .map((id) => SegmentId(id));
}

function rowToSegment(
  row: SegmentRow,
  skipped: SkippedPins,
  curatedByOrg: ReadonlyMap<OrgId, ReadonlySet<SegmentId>>
): Segment {
  const items = row.items ?? {};

  let spec: SkuPredicate;
  let include: EAN[];
  let exclude: EAN[];

  if (row.spec !== null && row.spec !== undefined) {
    // Spec-bearing row (frontend-owned): spec/include/exclude are authoritative; items is ignored.
    try {
      spec = parseSpec(row.spec);
    } catch (err) {
      throw new Error(
        `Segment "${row.id}": malformed spec -- ${err instanceof Error ? err.message : String(err)}`
      );
    }
    include = parseEanArray(row.include, row.id, 'include', skipped);
    exclude = parseEanArray(row.exclude, row.id, 'exclude', skipped);
  } else {
    // Legacy row (spec IS NULL, backend-owned): derive membership inputs from items.
    const rawSkus = Array.isArray(items.products) ? items.products : [];
    const rawBrands = Array.isArray(items.brands) ? items.brands : [];
    // Legacy pins were written from catalog keys, so they carry the stored mask.
    include = decodePins(
      rawSkus.filter((e): e is string => typeof e === 'string'),
      skipped
    );
    const brands = rawBrands
      .filter((b): b is string => typeof b === 'string')
      .map((b) => BrandSlug(b));
    exclude = [];
    spec = brands.length > 0 ? { kind: 'brand-in', slugs: brands } : false;
  }

  const id = SegmentId(row.id);
  return Segment({
    id,
    alias: row.brandalias?.trim() || 'Jämförelse',
    owner: row.user_id ? UserId(row.user_id) : null,
    org: row.org_id ? OrgId(row.org_id) : null,
    sharedWithOrg: row.shared_with_org === true,
    spec,
    include,
    exclude,
    provenance: row.org_id && curatedByOrg.get(OrgId(row.org_id))?.has(id) ? 'curated' : 'authored',
  });
}

/**
 * Who pinned each EAN, keyed by the real code. Attribution of manual edits, not
 * membership -- read on demand.
 *
 * added_by keys carry the stored mask: segment_modify_pins keys them by its p_eans
 * parameter, which this ACL sends masked, and every historic row was written the same way.
 * The tryEAN fallback is the one tolerant ingress in this file -- real-keyed entries that
 * buildItemsJson wrote between 634e9321 and the masked-key cutover exist in dev/test rows,
 * while the masked form is the contract going forward. A key that parses neither way is
 * skipped: attribution is a label on a row, so a lost one blanks a column rather than
 * changing membership, and there is no read that could tell the two forms apart anyway.
 */
function rowToAttribution(row: { items: SegmentRow['items'] }): ReadonlyMap<EAN, UserId> {
  const raw = row.items?.added_by;
  if (!raw || typeof raw !== 'object') return new Map();
  const out = new Map<EAN, UserId>();
  for (const [key, uid] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof uid !== 'string' || uid.trim() === '') continue;
    const ean = fromMasked(key) ?? tryEAN(key);
    if (ean) out.set(ean, UserId(uid));
  }
  return out;
}

function parseEanArray(raw: unknown, rowId: string, field: string, skipped: SkippedPins): EAN[] {
  if (raw === null || raw === undefined) return [];
  if (!Array.isArray(raw)) {
    throw new Error(
      `Segment "${rowId}": ${field} must be an array or null, got ${JSON.stringify(raw)}`
    );
  }
  return decodePins(
    raw.filter((e): e is string => typeof e === 'string'),
    skipped
  );
}

/**
 * The real EANs behind stored pins. These columns hold catalog keys, which carry the
 * stored mask (prod survey 2026-08-31); a pin without the marker is a contract
 * violation, counted for the read's single report rather than read as a real code.
 */
function decodePins(raw: readonly string[], skipped: SkippedPins): EAN[] {
  const eans: EAN[] = [];
  for (const value of raw) {
    const ean = fromMasked(value);
    if (ean) eans.push(ean);
    else skipped.count++;
  }
  return eans;
}

/**
 * The stored form of domain pins. The pin columns (include, exclude, items.products)
 * and the segment_modify_pins RPC all speak the masked vocabulary that decodePins
 * requires, so every write out of this ACL encodes -- a real EAN written into those
 * columns is dropped and reported by the very next read.
 */
function storedPins(eans: Iterable<EAN>): MaskedEan[] {
  return Array.from(eans, toMasked);
}

function buildItemsJson(input: {
  skus: readonly EAN[];
  brands: readonly BrandSlug[];
  addedBy: ReadonlyMap<EAN, UserId> | null;
}): Record<string, unknown> {
  // added_by shares the stored vocabulary of the pin columns: segment_modify_pins keys it
  // by the masked p_eans this ACL sends, so a write from here has to match that key form.
  const added_by: Record<string, string> = {};
  if (input.addedBy) {
    for (const [ean, uid] of input.addedBy) added_by[toMasked(ean)] = uid;
  }
  return {
    products: storedPins(input.skus),
    brands: input.brands as readonly string[],
    added_by,
  };
}

function slugify(alias: string): string {
  return alias
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}
